/**
 * WhatsApp 인증 훅
 * WhatsApp OTP 발송 및 검증 로직을 관리
 *
 * @see BE #297 - WhatsApp 휴대폰 인증 로그인
 */

import { useState, useCallback } from 'react';
import { whatsappAuthApi, ApiClientError } from '@/lib/api';
import type { AuthUser, VerifyWhatsAppOtpResponse } from '@/lib/api/types';
import type { Language, Locale } from '@/config/locales';

export interface UseWhatsAppAuthReturn {
  // OTP 발송 상태
  isSendingOtp: boolean;
  sendOtpError: string | null;
  otpExpiresAt: string | null;
  retryAfter: number | null;

  // OTP 검증 상태
  isVerifying: boolean;
  verifyError: string | null;
  attemptsRemaining: number | null;

  // 핸들러
  sendOtp: (phoneNumber: string) => Promise<{ success: boolean; error: string | null }>;
  verifyOtp: (phoneNumber: string, otp: string) => Promise<VerifyWhatsAppOtpResponse | null>;
  clearErrors: () => void;

  // 결과
  user: AuthUser | null;
  isNewUser: boolean;
}

// #522: Language(3) → Locale(15) 확장 — 에러 메시지 15언어 전수 지원
export function useWhatsAppAuth(language: Locale = 'es'): UseWhatsAppAuthReturn {
  // OTP 발송 상태
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  // OTP 검증 상태
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  // 결과
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);

  /**
   * OTP 발송
   * @param phoneNumber E.164 형식 전화번호 (예: +521234567890)
   * @returns 성공 여부와 에러 메시지
   */
  const sendOtp = useCallback(async (phoneNumber: string): Promise<{ success: boolean; error: string | null }> => {
    setIsSendingOtp(true);
    setSendOtpError(null);
    setRetryAfter(null);

    try {
      const response = await whatsappAuthApi.sendOtp(phoneNumber, language);

      if (response.success) {
        setOtpExpiresAt(response.expires_at || null);
        setRetryAfter(response.retry_after || null);
        return { success: true, error: null };
      } else {
        // 에러 처리
        const errorMessage = getErrorMessage(response.error, response.message, language);
        setSendOtpError(errorMessage);
        setRetryAfter(response.retry_after || null);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      let errorMessage: string;
      if (error instanceof ApiClientError && error.code) {
        // ApiClientError의 code를 사용해 다국어 에러 메시지 조회
        errorMessage = getErrorMessage(error.code, error.message, language);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = getDefaultErrorMessage(language);
      }
      setSendOtpError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSendingOtp(false);
    }
  }, [language]);

  /**
   * OTP 검증
   * @param phoneNumber E.164 형식 전화번호
   * @param otp 6자리 인증 코드
   * @returns 검증 결과 (성공 시 사용자 정보 포함)
   */
  const verifyOtp = useCallback(async (
    phoneNumber: string,
    otp: string
  ): Promise<VerifyWhatsAppOtpResponse | null> => {
    setIsVerifying(true);
    setVerifyError(null);
    setAttemptsRemaining(null);

    try {
      const response = await whatsappAuthApi.verifyOtp(phoneNumber, otp);

      if (response.success) {
        if (!response.user) {
          setUser(null);
          setIsNewUser(false);
          setVerifyError(getErrorMessage('AUTH_RESPONSE_INCOMPLETE', undefined, language));
          return null;
        }

        setUser(response.user);
        setIsNewUser(response.is_new_user || false);
        return response;
      } else {
        // 에러 처리
        const errorMessage = getErrorMessage(response.error, response.message, language);
        setVerifyError(errorMessage);
        setAttemptsRemaining(response.attempts_remaining ?? null);
        setRetryAfter(response.retry_after || null);
        return null;
      }
    } catch (error) {
      let errorMessage: string;
      if (error instanceof ApiClientError && error.code) {
        // ApiClientError의 code를 사용해 다국어 에러 메시지 조회
        errorMessage = getErrorMessage(error.code, error.message, language);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = getDefaultErrorMessage(language);
      }
      setVerifyError(errorMessage);
      return null;
    } finally {
      setIsVerifying(false);
    }
  }, [language]);

  /**
   * 에러 초기화
   */
  const clearErrors = useCallback(() => {
    setSendOtpError(null);
    setVerifyError(null);
    setAttemptsRemaining(null);
    setRetryAfter(null);
  }, []);

  return {
    // OTP 발송 상태
    isSendingOtp,
    sendOtpError,
    otpExpiresAt,
    retryAfter,

    // OTP 검증 상태
    isVerifying,
    verifyError,
    attemptsRemaining,

    // 핸들러
    sendOtp,
    verifyOtp,
    clearErrors,

    // 결과
    user,
    isNewUser,
  };
}

// #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
type LocalizedMessage =
  & Record<Language, string>
  & Record<Exclude<Locale, Language>, string>;

/**
 * 다국어 에러 메시지 맵
 */
const errorMessages: Record<string, LocalizedMessage> = {
  TOO_MANY_REQUESTS: {
    ko: '잠시 후 다시 시도해주세요.',
    es: 'Por favor, inténtalo de nuevo más tarde.',
    en: 'Please try again later.',
    pt: 'Tente novamente mais tarde.',
    fr: 'Veuillez réessayer plus tard.',
    zh: '请稍后再试。',
    ja: 'しばらくしてからもう一度お試しください。',
    hi: 'कृपया कुछ देर बाद फिर से प्रयास करें।',
    de: 'Bitte versuchen Sie es später erneut.',
    it: 'Riprova più tardi.',
    ru: 'Повторите попытку позже.',
    ar: 'يرجى المحاولة مرة أخرى لاحقًا.',
    vi: 'Vui lòng thử lại sau.',
    th: 'กรุณาลองอีกครั้งในภายหลัง',
    nl: 'Probeer het later opnieuw.',
  },
  TOO_MANY_FAILED_ATTEMPTS: {
    ko: '인증 시도 횟수를 초과했습니다. 잠시 후 다시 시도해주세요.',
    es: 'Has excedido el número de intentos. Por favor, inténtalo más tarde.',
    en: 'Too many failed attempts. Please try again later.',
    pt: 'Você excedeu o número de tentativas. Tente novamente mais tarde.',
    fr: 'Vous avez dépassé le nombre de tentatives. Veuillez réessayer plus tard.',
    zh: '已超过尝试次数，请稍后再试。',
    ja: '認証の試行回数を超えました。しばらくしてからもう一度お試しください。',
    hi: 'आपने प्रयासों की सीमा पार कर ली है। कृपया बाद में फिर से प्रयास करें।',
    de: 'Sie haben die Anzahl der Versuche überschritten. Bitte versuchen Sie es später erneut.',
    it: 'Hai superato il numero di tentativi. Riprova più tardi.',
    ru: 'Превышено число попыток. Повторите попытку позже.',
    ar: 'لقد تجاوزت عدد المحاولات. يرجى المحاولة لاحقًا.',
    vi: 'Bạn đã vượt quá số lần thử. Vui lòng thử lại sau.',
    th: 'คุณพยายามเกินจำนวนครั้งที่กำหนด กรุณาลองใหม่ภายหลัง',
    nl: 'U heeft het aantal pogingen overschreden. Probeer het later opnieuw.',
  },
  SERVICE_UNAVAILABLE: {
    ko: 'WhatsApp 인증 서비스를 사용할 수 없습니다.',
    es: 'El servicio de autenticación de WhatsApp no está disponible.',
    en: 'WhatsApp authentication service is unavailable.',
    pt: 'O serviço de autenticação do WhatsApp não está disponível.',
    fr: "Le service d'authentification WhatsApp n'est pas disponible.",
    zh: 'WhatsApp验证服务不可用。',
    ja: 'WhatsApp認証サービスを利用できません。',
    hi: 'WhatsApp प्रमाणीकरण सेवा उपलब्ध नहीं है।',
    de: 'Der WhatsApp-Authentifizierungsdienst ist nicht verfügbar.',
    it: 'Il servizio di autenticazione WhatsApp non è disponibile.',
    ru: 'Сервис аутентификации WhatsApp недоступен.',
    ar: 'خدمة المصادقة عبر WhatsApp غير متاحة.',
    vi: 'Dịch vụ xác thực WhatsApp không khả dụng.',
    th: 'บริการยืนยันตัวตนผ่าน WhatsApp ไม่พร้อมใช้งาน',
    nl: 'De WhatsApp-verificatieservice is niet beschikbaar.',
  },
  SEND_FAILED: {
    ko: 'OTP 발송에 실패했습니다.',
    es: 'Error al enviar el código OTP.',
    en: 'Failed to send OTP.',
    pt: 'Falha ao enviar o código OTP.',
    fr: "Échec de l'envoi du code OTP.",
    zh: '发送OTP验证码失败。',
    ja: 'OTPコードの送信に失敗しました。',
    hi: 'OTP कोड भेजने में विफल।',
    de: 'Fehler beim Senden des OTP-Codes.',
    it: "Errore nell'invio del codice OTP.",
    ru: 'Не удалось отправить код OTP.',
    ar: 'فشل في إرسال رمز OTP.',
    vi: 'Gửi mã OTP thất bại.',
    th: 'ส่งรหัส OTP ไม่สำเร็จ',
    nl: 'Verzenden van de OTP-code mislukt.',
  },
  OTP_EXPIRED: {
    ko: '인증 코드가 만료되었습니다. 다시 요청해주세요.',
    es: 'El código ha expirado. Por favor, solicita uno nuevo.',
    en: 'Verification code has expired. Please request a new one.',
    pt: 'O código expirou. Solicite um novo.',
    fr: 'Le code a expiré. Veuillez en demander un nouveau.',
    zh: '验证码已过期，请重新获取。',
    ja: '認証コードの有効期限が切れました。再度リクエストしてください。',
    hi: 'कोड की अवधि समाप्त हो गई है। कृपया नया अनुरोध करें।',
    de: 'Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.',
    it: 'Il codice è scaduto. Richiedine uno nuovo.',
    ru: 'Срок действия кода истёк. Запросите новый.',
    ar: 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
    vi: 'Mã đã hết hạn. Vui lòng yêu cầu mã mới.',
    th: 'รหัสหมดอายุแล้ว กรุณาขอรหัสใหม่',
    nl: 'De code is verlopen. Vraag een nieuwe aan.',
  },
  INVALID_OTP: {
    ko: '인증 코드가 올바르지 않습니다.',
    es: 'El código de verificación es incorrecto.',
    en: 'Invalid verification code.',
    pt: 'O código de verificação está incorreto.',
    fr: 'Le code de vérification est incorrect.',
    zh: '验证码不正确。',
    ja: '認証コードが正しくありません。',
    hi: 'सत्यापन कोड गलत है।',
    de: 'Der Bestätigungscode ist ungültig.',
    it: 'Il codice di verifica non è corretto.',
    ru: 'Неверный код подтверждения.',
    ar: 'رمز التحقق غير صحيح.',
    vi: 'Mã xác minh không đúng.',
    th: 'รหัสยืนยันไม่ถูกต้อง',
    nl: 'De verificatiecode is onjuist.',
  },
  LOCKED: {
    ko: '인증 시도 횟수를 초과했습니다.',
    es: 'Has excedido el número de intentos.',
    en: 'Too many attempts.',
    pt: 'Você excedeu o número de tentativas.',
    fr: 'Vous avez dépassé le nombre de tentatives.',
    zh: '已超过尝试次数。',
    ja: '認証の試行回数を超えました。',
    hi: 'आपने प्रयासों की सीमा पार कर ली है।',
    de: 'Sie haben die Anzahl der Versuche überschritten.',
    it: 'Hai superato il numero di tentativi.',
    ru: 'Превышено число попыток.',
    ar: 'لقد تجاوزت عدد المحاولات.',
    vi: 'Bạn đã vượt quá số lần thử.',
    th: 'คุณพยายามเกินจำนวนครั้งที่กำหนด',
    nl: 'U heeft het aantal pogingen overschreden.',
  },
  MAX_ATTEMPTS_EXCEEDED: {
    ko: '인증 시도 횟수를 초과했습니다.',
    es: 'Has excedido el número de intentos.',
    en: 'Maximum attempts exceeded.',
    pt: 'Você excedeu o número de tentativas.',
    fr: 'Vous avez dépassé le nombre de tentatives.',
    zh: '已超过最大尝试次数。',
    ja: '認証の試行回数を超えました。',
    hi: 'आपने प्रयासों की अधिकतम सीमा पार कर ली है।',
    de: 'Maximale Anzahl an Versuchen überschritten.',
    it: 'Numero massimo di tentativi superato.',
    ru: 'Превышено максимальное число попыток.',
    ar: 'تم تجاوز الحد الأقصى للمحاولات.',
    vi: 'Đã vượt quá số lần thử tối đa.',
    th: 'เกินจำนวนครั้งสูงสุดที่อนุญาต',
    nl: 'Maximaal aantal pogingen overschreden.',
  },
  // 네트워크 관련 에러 (ApiClientError)
  NETWORK_ERROR: {
    ko: '네트워크 연결을 확인해주세요.',
    es: 'Por favor, verifica tu conexión a internet.',
    en: 'Please check your network connection.',
    pt: 'Verifique sua conexão com a internet.',
    fr: 'Veuillez vérifier votre connexion Internet.',
    zh: '请检查您的网络连接。',
    ja: 'ネットワーク接続をご確認ください。',
    hi: 'कृपया अपना नेटवर्क कनेक्शन जांचें।',
    de: 'Bitte überprüfen Sie Ihre Netzwerkverbindung.',
    it: 'Verifica la tua connessione a internet.',
    ru: 'Проверьте подключение к сети.',
    ar: 'يرجى التحقق من اتصالك بالإنترنت.',
    vi: 'Vui lòng kiểm tra kết nối mạng.',
    th: 'กรุณาตรวจสอบการเชื่อมต่อเครือข่าย',
    nl: 'Controleer uw netwerkverbinding.',
  },
  TIMEOUT: {
    ko: '요청 시간이 초과되었습니다. 다시 시도해주세요.',
    es: 'La solicitud ha expirado. Por favor, inténtalo de nuevo.',
    en: 'Request timed out. Please try again.',
    pt: 'A solicitação expirou. Tente novamente.',
    fr: 'La requête a expiré. Veuillez réessayer.',
    zh: '请求超时，请重试。',
    ja: 'リクエストがタイムアウトしました。もう一度お試しください。',
    hi: 'अनुरोध का समय समाप्त हो गया। कृपया फिर से प्रयास करें।',
    de: 'Zeitüberschreitung der Anfrage. Bitte versuchen Sie es erneut.',
    it: 'La richiesta è scaduta. Riprova.',
    ru: 'Время запроса истекло. Повторите попытку.',
    ar: 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.',
    vi: 'Yêu cầu đã hết thời gian chờ. Vui lòng thử lại.',
    th: 'คำขอหมดเวลา กรุณาลองอีกครั้ง',
    nl: 'Time-out van het verzoek. Probeer het opnieuw.',
  },
  CSRF_INIT_ERROR: {
    ko: '보안 초기화에 실패했습니다. 페이지를 새로고침해주세요.',
    es: 'Error de inicialización de seguridad. Por favor, actualiza la página.',
    en: 'Security initialization failed. Please refresh the page.',
    pt: 'Falha na inicialização de segurança. Atualize a página.',
    fr: "Échec de l'initialisation de sécurité. Veuillez actualiser la page.",
    zh: '安全初始化失败，请刷新页面。',
    ja: 'セキュリティの初期化に失敗しました。ページを更新してください。',
    hi: 'सुरक्षा आरंभीकरण विफल। कृपया पेज रीफ़्रेश करें।',
    de: 'Sicherheitsinitialisierung fehlgeschlagen. Bitte aktualisieren Sie die Seite.',
    it: 'Inizializzazione di sicurezza non riuscita. Aggiorna la pagina.',
    ru: 'Ошибка инициализации безопасности. Обновите страницу.',
    ar: 'فشل في تهيئة الأمان. يرجى تحديث الصفحة.',
    vi: 'Khởi tạo bảo mật thất bại. Vui lòng tải lại trang.',
    th: 'การเริ่มต้นระบบความปลอดภัยล้มเหลว กรุณารีเฟรชหน้า',
    nl: 'Beveiligingsinitialisatie mislukt. Vernieuw de pagina.',
  },
  AUTH_RESPONSE_INCOMPLETE: {
    ko: '로그인 응답을 확인할 수 없습니다. 다시 시도해주세요.',
    es: 'No pudimos confirmar tu inicio de sesión. Inténtalo de nuevo.',
    en: 'We could not confirm your login. Please try again.',
    pt: 'Não foi possível confirmar seu login. Tente novamente.',
    fr: "Nous n'avons pas pu confirmer votre connexion. Veuillez réessayer.",
    zh: '无法确认您的登录，请重试。',
    ja: 'ログインを確認できませんでした。もう一度お試しください。',
    hi: 'हम आपका लॉगिन पुष्टि नहीं कर सके। कृपया फिर से प्रयास करें।',
    de: 'Ihre Anmeldung konnte nicht bestätigt werden. Bitte versuchen Sie es erneut.',
    it: 'Non siamo riusciti a confermare il tuo accesso. Riprova.',
    ru: 'Не удалось подтвердить вход. Повторите попытку.',
    ar: 'تعذر تأكيد تسجيل الدخول. يرجى المحاولة مرة أخرى.',
    vi: 'Không thể xác nhận đăng nhập của bạn. Vui lòng thử lại.',
    th: 'ไม่สามารถยืนยันการเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง',
    nl: 'We konden uw login niet bevestigen. Probeer het opnieuw.',
  },
};

const defaultErrors: LocalizedMessage = {
  ko: '오류가 발생했습니다.',
  es: 'Ocurrió un error.',
  en: 'An error occurred.',
  pt: 'Ocorreu um erro.',
  fr: 'Une erreur est survenue.',
  zh: '发生了错误。',
  ja: 'エラーが発生しました。',
  hi: 'एक त्रुटि हुई।',
  de: 'Ein Fehler ist aufgetreten.',
  it: 'Si è verificato un errore.',
  ru: 'Произошла ошибка.',
  ar: 'حدث خطأ.',
  vi: 'Đã xảy ra lỗi.',
  th: 'เกิดข้อผิดพลาด',
  nl: 'Er is een fout opgetreden.',
};

/**
 * 기본 에러 메시지 반환 (#522: Locale 확장)
 */
function getDefaultErrorMessage(language: Locale): string {
  return defaultErrors[language] || defaultErrors.es;
}

/**
 * 에러 코드를 사용자 친화적 메시지로 변환 (#522: Locale 확장)
 */
function getErrorMessage(errorCode?: string, defaultMessage?: string, language: Locale = 'es'): string {
  if (errorCode && errorMessages[errorCode]) {
    return errorMessages[errorCode][language] || errorMessages[errorCode].es;
  }
  return defaultMessage || getDefaultErrorMessage(language);
}
